import api from "@/lib/axios";

const candidateService = {
  getAllCandidates: async () => {
    try {
      const response = await api.get('/candidates');
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch candidates: " + error.message);
    }
  },
  getCandidateListing: async (filters) => {
    try {
      const response = await api.get('/candidate/listing', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch candidate listings: " + error.message);
    }
  },
  getCandidateById: async (id) => {
    try {
      const response = await api.get(`/candidate/${id}/applications`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch candidate ${id}: ` + error.message);
    }
  },

  createCandidate: async (candidateData) => {
    try {
      const response = await api.post('/candidates', candidateData);
      return response.data;
    } catch (error) {
      throw new Error("Failed to create candidate: " + error.message);
    }
  },

  updateCandidate: async (id, candidateData) => {
    try {
      const response = await api.put(`/candidates/${id}`, candidateData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update candidate ${id}: ` + error.message);
    }
  },

  deleteCandidate: async (id) => {
    try {
      await api.delete(`/candidate/${id}`);
      return true;
    } catch (error) {
      throw new Error(`Failed to delete candidate ${id}: ` + error.message);
    }
  },

  bulkDeleteCandidates: async (candidateIds) => {
    try {
      const response = await api.post('/candidate/bulk-delete', { candidate_ids: candidateIds });
      return response.data;
    } catch (error) {
      throw new Error("Failed to delete candidates in bulk: " + error.message);
    }
  },

  personalFields: async () => {
    try {
      const response = await api.get('/personal-fields');
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch personal hifon fields: " + error.message);
    }
  },

  // Candidate-specific APIs (for logged-in candidates)
  getCandidateProfile: async () => {
    try {
      const response = await api.get(`/candidate/profile`);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch candidate profile: " + error.message);
    }
  },

  getCandidateApplications: async () => {
    try {
      const response = await api.get(`/candidate/applications`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch candidate applications for candidate: ` + error.message);
    }
  },

  getCandidateApplicationById: async (applicationId) => {
    try {
      const response = await api.get(`/candidate/applications/${applicationId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch candidate application ${applicationId}: ` + error.message);
    }
  },

  sendContactRequest: async (payload) => {
    try {
      const response = await api.post('/scheduling/candidate/contact-request', payload);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      throw new Error(message);
    }
  },

  /** Chat history for one contact request */
  getCandidateContactRequestMessages: async (contactRequestId) => {
    try {
      const response = await api.get(
        `notifications/contact-requests/${contactRequestId}/messages`,
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      throw new Error(message || "Failed to fetch contact request messages");
    }
  },

  /**
   * Candidate's contact-request rows (needs backend route). Tries common paths.
   */
  sendChatNotMessage: async ({ candidate_id, message, conversation_history }) => {
    try {
      const response = await api.post('/chatnot', {
        candidate_id,
        message,
        conversation_history,
      });
      return response.data;
    } catch (error) {
      const errMessage = error.response?.data?.message || error.message;
      throw new Error(errMessage || 'Failed to send chat message');
    }
  },

  listCandidateContactRequests: async () => {
    const attempts = [
      () => api.get("scheduling/candidate/contact-request"),
      () => api.get("notifications/contact-requests"),
    ];
    let lastErr;
    for (const attempt of attempts) {
      try {
        const response = await attempt();
        return response.data;
      } catch (error) {
        lastErr = error;
      }
    }
    const message =
      lastErr?.response?.data?.message || lastErr?.message || "Failed to fetch contact requests";
    throw new Error(message);
  },
};


export default candidateService;